import TitrePage from "../components/charte/TitrePage";
import * as React from 'react';
import { noterActivite, EXPORTS } from '../utilities/journalActivite';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { useEffect, useMemo, useState } from 'react';
import * as Papa from 'papaparse';
import {
  Box, Button, Chip, Dialog, DialogContent, DialogTitle,
  IconButton, MenuItem, Modal, Select, Tab, Tabs, TextField, Typography,
} from '@mui/material';
import { loaderOff, loaderOn, logout, openSnackbar } from '../redux/actions';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import store from '../redux/store';
import locationApi from '../utilities/locationApi';
import { getPositions } from '../utilities/locationService';
import { sujetCourant } from '../api/role';
import DownloadIcon from '@mui/icons-material/Download';
import PictureAsPdfRoundedIcon from '@mui/icons-material/PictureAsPdfRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import ScheduleRoundedIcon from '@mui/icons-material/ScheduleRounded';
import { jsPDF } from 'jspdf';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell,
} from 'recharts';
import { AIDE_ANALYSES } from '../aide/textesAide';

/* ─── Design tokens ─────────────────────────────────────────────────────────── */
/* Couleurs de la charte (src/theme/tribeTheme.ts), mêmes clés qu'avant. */
const C = {
  bg: 'var(--c-bg)',
  card: 'var(--c-surface)',
  card2: 'var(--c-surface-2)',
  border: 'var(--c-border)',
  borderAccent: '#1F1E1B',
  accent: '#1F1E1B',
  accentLight: '#57534A',
  accentMuted: 'rgba(31,30,27,0.07)',
  text: '#1F1E1B',
  textSub: '#57534A',
  textMuted: '#57534A',
  success: '#356A0F',
  warning: '#7C5600',
  error: '#A23A0D',
} as const;

/* ─── Freshness helpers ──────────────────────────────────────────────────────── */
/* Seuils propres à cet écran (< 30 j, ≤ 90 j) : les seuils de Configuration ▸
   Fraîcheur ne sont lus par aucun service ni par l'app (question ouverte). */
type FreshnessLevel = 'frais' | 'vieillissant' | 'périmé';

function getFreshness(dateStr: string): FreshnessLevel {
  try {
    const ageDays = (Date.now() - new Date(dateStr).getTime()) / 86_400_000;
    if (ageDays < 30) return 'frais';
    if (ageDays <= 90) return 'vieillissant';
    return 'périmé';
  } catch { return 'périmé'; }
}

const FRESH_COLOR: Record<FreshnessLevel, string> = {
  frais: '#356A0F', vieillissant: '#7C5600', périmé: '#A23A0D',
};
const FRESH_BG: Record<FreshnessLevel, string> = {
  frais: 'rgba(53,106,15,0.12)', vieillissant: 'rgba(124,86,0,0.12)', périmé: 'rgba(162,58,13,0.12)',
};

/* ─── Helpers ────────────────────────────────────────────────────────────────── */
/* POI approuvé : même définition que les statistiques du serveur. */
function estApprouve(statut: any): boolean {
  return /^(approved|validated)$/i.test(String(statut ?? ''));
}

function groupByWeek(rows: any[]): { week: string; count: number }[] {
  const map = new Map<string, number>();
  rows.forEach(r => {
    try {
      const d = new Date(r.createdOnRaw);
      const monday = new Date(d);
      monday.setDate(d.getDate() - d.getDay() + 1);
      const key = monday.toISOString().slice(0, 10);
      map.set(key, (map.get(key) ?? 0) + 1);
    } catch { /* skip */ }
  });
  return Array.from(map.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([week, count]) => ({ week, count }));
}

/* ─── Sub-components ─────────────────────────────────────────────────────────── */
function SummaryCard({ label, value, sub }: { label: string; value: string | number; sub?: string }) {
  return (
    <Box sx={{
      background: C.card, border: `1px solid ${C.borderAccent}`,
      borderRadius: '8px', p: '18px 22px', flex: 1, minWidth: 0,
    }}>
      <Typography sx={{ color: C.textMuted, fontSize: '12px', mb: 0.5 }}>{label}</Typography>
      <Typography sx={{ color: C.accentLight, fontSize: '26px', fontWeight: 700, lineHeight: 1.2 }}>
        {value}
      </Typography>
      {sub && <Typography sx={{ color: C.textMuted, fontSize: '11px', mt: 0.5 }}>{sub}</Typography>}
    </Box>
  );
}

function FreshBar({ frais, vieillissant, périmé }: { frais: number; vieillissant: number; périmé: number }) {
  const total = frais + vieillissant + périmé;
  if (total === 0) return null;
  const pF = (frais / total) * 100;
  const pV = (vieillissant / total) * 100;
  const pP = (périmé / total) * 100;
  return (
    <Box>
      <Box sx={{ display: 'flex', height: 10, borderRadius: '5px', overflow: 'hidden', mb: 1 }}>
        {pF > 0 && <Box sx={{ width: `${pF}%`, background: '#356A0F' }} />}
        {pV > 0 && <Box sx={{ width: `${pV}%`, background: '#7C5600' }} />}
        {pP > 0 && <Box sx={{ width: `${pP}%`, background: '#A23A0D' }} />}
      </Box>
      <Box sx={{ display: 'flex', gap: 2 }}>
        {[{ label: 'Frais', v: frais, c: '#356A0F' }, { label: 'Vieillissant', v: vieillissant, c: '#7C5600' }, { label: 'Périmé', v: périmé, c: '#A23A0D' }].map(({ label, v, c }) => (
          <Box key={label} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box sx={{ width: 8, height: 8, borderRadius: '50%', background: c }} />
            <Typography sx={{ fontSize: '11px', color: C.textMuted }}>{label}: {v}</Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
}

const inputSx = {
  '& .MuiOutlinedInput-root': {
    background: C.card2, color: C.text, borderRadius: '8px',
    '& fieldset': { borderColor: C.border },
    '&:hover fieldset': { borderColor: C.borderAccent },
    '&.Mui-focused fieldset': { borderColor: C.accentLight },
  },
  '& .MuiInputLabel-root': { color: C.textMuted },
  '& .MuiInputLabel-root.Mui-focused': { color: C.accentLight },
  '& input': { color: C.text },
};

/* ─── Main component ──────────────────────────────────────────────────────────── */
export default function ExtractData() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const isLoggedIn = useSelector((state: any) => state.auth.isLoggedIn);
  const isTokenExpired = useSelector((state: any) => state.auth.isTokenExpired);

  const [respData, setRespdata] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(0);

  /* ── Export filters ── */
  const [filterType, setFilterType] = useState('');
  const [filterZone, setFilterZone] = useState('');
  const [filterCollecteur, setFilterCollecteur] = useState('');
  const [filterDateStart, setFilterDateStart] = useState('');
  const [filterDateEnd, setFilterDateEnd] = useState('');
  const [filterFreshness, setFilterFreshness] = useState('');

  /* ── Type drill-down modal ── */
  const [drillType, setDrillType] = useState<string | null>(null);


  /* ── Fetch ── */
  useEffect(() => {
    // Positions légères (sans images ni réponses) au lieu de toute la
    // collection : seuls les POI approuvés sont exportés.
    const fetchData = async () => {
      dispatch(loaderOn());
      setLoading(true);
      try {
        setRespdata((await getPositions()).filter((p: any) => estApprouve(p.status)));
      } catch {
        dispatch(openSnackbar('Erreur chargement données', 'red'));
      } finally {
        setLoading(false);
        dispatch(loaderOff());
      }
    };
    fetchData();
  }, []);

  /* ── Normalized rows ── */
  const rows = useMemo(() => respData.map((val: any, idx: number) => {
    const rawDate = val.createdOn ?? '';
    const freshness: FreshnessLevel = getFreshness(rawDate);
    return {
      id: idx + 1,
      placekey: val.placekey ?? '',
      locationName: val.locationName ?? '',
      locationType: val.locationType ?? '',
      latitude: val.latitude ?? '',
      longitude: val.longitude ?? '',
      address: val.address ?? '',
      createdOnRaw: rawDate,
      createdOn: rawDate ? new Date(rawDate).toLocaleString('fr-FR') : '',
      createdBy: val.createdBy ?? '',
      freshness,
      // Ville résolue par le serveur au géocodage, jamais devinée dans l'adresse.
      city: val.city || 'Inconnue',
    };
  }), [respData]);

  /* ── Filtered rows for export ── */
  const filteredRows = useMemo(() => rows.filter(r => {
    if (filterType && r.locationType !== filterType) return false;
    if (filterZone && !r.city.toLowerCase().includes(filterZone.toLowerCase())) return false;
    if (filterCollecteur && !r.createdBy.toLowerCase().includes(filterCollecteur.toLowerCase())) return false;
    if (filterFreshness && r.freshness !== filterFreshness) return false;
    if (filterDateStart && new Date(r.createdOnRaw) < new Date(filterDateStart)) return false;
    if (filterDateEnd) {
      const end = new Date(filterDateEnd); end.setHours(23, 59, 59, 999);
      if (new Date(r.createdOnRaw) > end) return false;
    }
    return true;
  }), [rows, filterType, filterZone, filterCollecteur, filterFreshness, filterDateStart, filterDateEnd]);

  /* ── Aggregates ── */
  const freshCounts = useMemo(() => ({
    frais: rows.filter(r => r.freshness === 'frais').length,
    vieillissant: rows.filter(r => r.freshness === 'vieillissant').length,
    périmé: rows.filter(r => r.freshness === 'périmé').length,
  }), [rows]);

  const typeMap = useMemo(() => {
    const m = new Map<string, { total: number; frais: number; vieillissant: number; périmé: number }>();
    rows.forEach(r => {
      const key = r.locationType || 'Inconnu';
      const existing = m.get(key) ?? { total: 0, frais: 0, vieillissant: 0, périmé: 0 };
      existing.total++;
      existing[r.freshness]++;
      m.set(key, existing);
    });
    return Array.from(m.entries())
      .sort(([, a], [, b]) => b.total - a.total)
      .map(([type, stats]) => ({ type, ...stats, pct: rows.length > 0 ? Math.round((stats.total / rows.length) * 100) : 0 }));
  }, [rows]);

  const cityMap = useMemo(() => {
    const m = new Map<string, { count: number; collecteurs: Set<string>; lastDate: string }>();
    rows.forEach(r => {
      const key = r.city;
      const existing = m.get(key) ?? { count: 0, collecteurs: new Set(), lastDate: '' };
      existing.count++;
      if (r.createdBy) existing.collecteurs.add(r.createdBy);
      if (r.createdOnRaw > existing.lastDate) existing.lastDate = r.createdOnRaw;
      m.set(key, existing);
    });
    return Array.from(m.entries())
      .sort(([, a], [, b]) => b.count - a.count)
      .map(([city, s]) => ({ city, count: s.count, collecteurs: s.collecteurs.size, lastDate: s.lastDate ? new Date(s.lastDate).toLocaleDateString('fr-FR') : '—' }));
  }, [rows]);

  const collecteurMap = useMemo(() => {
    const m = new Map<string, { count: number; lastDate: string }>();
    rows.forEach(r => {
      if (!r.createdBy) return;
      const existing = m.get(r.createdBy) ?? { count: 0, lastDate: '' };
      existing.count++;
      if (r.createdOnRaw > existing.lastDate) existing.lastDate = r.createdOnRaw;
      m.set(r.createdBy, existing);
    });
    return Array.from(m.entries())
      .sort(([, a], [, b]) => b.count - a.count)
      .map(([name, s]) => ({ name, count: s.count, lastDate: s.lastDate ? new Date(s.lastDate).toLocaleDateString('fr-FR') : '—' }));
  }, [rows]);

  const weeklyData = useMemo(() => groupByWeek(filteredRows), [filteredRows]);

  const top3Types = typeMap.slice(0, 3);
  const collecteursCount = useMemo(() => new Set(rows.map(r => r.createdBy).filter(Boolean)).size, [rows]);
  const uniqueTypes = useMemo(() => typeMap.map(t => t.type), [typeMap]);

  /* ── Drill-down rows ── */
  const drillRows = useMemo(() => drillType ? rows.filter(r => r.locationType === drillType) : [], [rows, drillType]);
  const drillColumns: GridColDef[] = [
    { field: 'locationName', headerName: 'Nom', width: 200 },
    { field: 'address', headerName: 'Adresse', width: 280 },
    { field: 'createdBy', headerName: 'Spotter', width: 150 },
    { field: 'createdOn', headerName: 'Date', width: 160 },
    { field: 'freshness', headerName: 'Fraîcheur', width: 110,
      renderCell: (p) => <Chip label={p.value} size="small" sx={{ background: FRESH_BG[p.value as FreshnessLevel], color: FRESH_COLOR[p.value as FreshnessLevel], fontWeight: 600, fontSize: '11px' }} /> },
  ];

  /* ── Export helpers ── */
  const filterDescription = [
    filterType && `type:${filterType}`, filterZone && `zone:${filterZone}`,
    filterCollecteur && `spotter:${filterCollecteur}`, filterFreshness && `fraîcheur:${filterFreshness}`,
    filterDateStart && `depuis:${filterDateStart}`, filterDateEnd && `au:${filterDateEnd}`,
  ].filter(Boolean).join(', ') || 'aucun';

  const resetFilters = () => {
    setFilterType(''); setFilterZone(''); setFilterCollecteur('');
    setFilterFreshness(''); setFilterDateStart(''); setFilterDateEnd('');
  };

  /* Question 18 : deux exports. « client » sans l'email des Spotters (livrable
     remis au client), « interne » avec. Chaque export est tracé côté serveur
     AVANT le téléchargement : sans trace, pas de fichier. */
  const [typeExport, setTypeExport] = useState<'client' | 'interne'>('client');

  const tracerExport = async (format: 'csv' | 'geojson'): Promise<boolean> => {
    try {
      await locationApi.post('/exports/trace', {
        type: typeExport, format, lignes: filteredRows.length, filtres: filterDescription,
      });
      return true;
    } catch {
      dispatch(openSnackbar("Export refusé : il n'a pas pu être tracé.", 'red'));
      return false;
    }
  };

  const suffixe = () => `${typeExport}-${new Date().toISOString().slice(0, 10)}`;

  const handleExportCSV = async () => {
    if (filteredRows.length === 0) { dispatch(openSnackbar('Aucune donnée à exporter', '#1976d2')); return; }
    if (!(await tracerExport('csv'))) return;
    // Auteur lu dans le jeton de session (userEmail n'était écrit nulle part).
    const adminEmail = sujetCourant() || 'inconnu';
    const meta = `# Tribe Africa | Export ${typeExport} | Date: ${new Date().toISOString()} | Lieux: ${filteredRows.length} | Filtres: ${filterDescription} | Exporté par: ${adminEmail}\n`;
    const csv = meta + Papa.unparse(filteredRows.map(r => ({
      placekey: r.placekey, locationName: r.locationName, locationType: r.locationType,
      latitude: r.latitude, longitude: r.longitude, address: r.address,
      ...(typeExport === 'interne' ? { createdBy: r.createdBy } : {}),
      createdOn: r.createdOn, freshnessLabel: r.freshness,
    })));
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `tribe-export-${suffixe()}.csv`;
    link.click();
    noterActivite('export', EXPORTS.exportCibleCsv);
  };

  const handleExportGeoJSON = async () => {
    if (filteredRows.length === 0) { dispatch(openSnackbar('Aucune donnée à exporter', '#1976d2')); return; }
    if (!(await tracerExport('geojson'))) return;
    const geojson = {
      type: 'FeatureCollection',
      metadata: { source: 'Tribe Africa', export: typeExport, exportDate: new Date().toISOString(), count: filteredRows.length, filters: filterDescription },
      features: filteredRows.map(r => ({
        type: 'Feature',
        geometry: { type: 'Point', coordinates: [parseFloat(r.longitude), parseFloat(r.latitude)] },
        properties: {
          name: r.locationName, category: r.locationType,
          ...(typeExport === 'interne' ? { collecteur: r.createdBy } : {}),
          date: r.createdOn, freshness: r.freshness, address: r.address,
        },
      })),
    };
    const blob = new Blob([JSON.stringify(geojson, null, 2)], { type: 'application/json' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `tribe-export-${suffixe()}.geojson`;
    link.click();
    noterActivite('export', EXPORTS.exportCibleGeojson);
    URL.revokeObjectURL(link.href);
  };

  const choixTypeExport = (
    <select value={typeExport} onChange={e => setTypeExport(e.target.value as 'client' | 'interne')}
      aria-label="Type d'export"
      style={{ background: C.card, color: C.text, border: `1px solid ${C.borderAccent}`, borderRadius: 6, padding: '6px 10px', fontSize: 13 }}>
      <option value="client">Export client (sans emails)</option>
      <option value="interne">Export interne (avec emails)</option>
    </select>
  );

  const handleExportPDF = () => {
    const doc = new jsPDF({ orientation: 'portrait', format: 'a4' });
    const today = new Date().toLocaleDateString('fr-FR');
    doc.setFontSize(18); doc.setTextColor(124, 58, 237);
    doc.text('Tribe Africa — Rapport de couverture terrain', 14, 18);
    doc.setFontSize(9); doc.setTextColor(100, 116, 139);
    doc.text(`Date : ${today}  |  Filtres : ${filterDescription}`, 14, 26);
    doc.setFontSize(11); doc.setTextColor(50, 50, 70);
    doc.text('STATISTIQUES GÉNÉRALES', 14, 38);
    doc.setFontSize(9);
    doc.text(`Total lieux : ${filteredRows.length}`, 14, 46);
    doc.text(`Spotters contributeurs (au moins un lieu approuvé) : ${collecteursCount}`, 14, 52);
    doc.text(`Frais (<30j) : ${freshCounts.frais} · Vieillissant : ${freshCounts.vieillissant} · Périmé : ${freshCounts.périmé}`, 14, 58);
    doc.setFontSize(11); doc.setTextColor(50, 50, 70);
    doc.text('TOP 10 ZONES DE COUVERTURE', 14, 70);
    doc.setFontSize(8); doc.setTextColor(80, 80, 100);
    cityMap.slice(0, 10).forEach(({ city, count, collecteurs }, i) => {
      doc.text(`${i + 1}. ${city} — ${count} lieux · ${collecteurs} Spotters`, 14, 78 + i * 7);
    });
    doc.setFontSize(11); doc.setTextColor(50, 50, 70);
    doc.text('RÉPARTITION PAR TYPE', 14, 150);
    doc.setFontSize(8); doc.setTextColor(80, 80, 100);
    typeMap.slice(0, 10).forEach(({ type, total, pct }, i) => {
      doc.text(`${type} : ${total} (${pct}%)`, 14, 158 + i * 7);
    });
    doc.setFontSize(7); doc.setTextColor(150);
    doc.text('Confidentiel — Tribe Africa', 14, 285);
    doc.save(`rapport-couverture-${new Date().toISOString().slice(0, 10)}.pdf`);
    noterActivite('export', EXPORTS.exportRapportPdf);
  };

  /* ─── RENDER ─────────────────────────────────────────────────────────────── */
  return (
    <Box sx={{ p: '28px 32px', minHeight: '100vh', background: C.bg, color: C.text }}>

      {/* ── Header ── */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3.5 }}>
        <Box>
          <TitrePage domaine="analyses" aide={AIDE_ANALYSES.exports}>Exports et rapports</TitrePage>
          <Typography sx={{ fontSize: '13px', color: C.textMuted, mt: 0.5 }}>
            Volumes par type, zone, Spotter et période ; export ciblé ; rapport de couverture pour les clients.
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
          {choixTypeExport}
          <Button variant="contained" onClick={handleExportCSV} startIcon={<DownloadIcon />}
            sx={{ background: C.accent, borderRadius: '8px', textTransform: 'none', fontWeight: 600, '&:hover': { background: C.accentLight } }}>
            Exporter CSV
          </Button>
          <Button variant="outlined" onClick={handleExportGeoJSON} startIcon={<DownloadIcon />}
            sx={{ borderColor: C.borderAccent, color: C.accentLight, borderRadius: '8px', textTransform: 'none', fontWeight: 600, '&:hover': { borderColor: C.accentLight, background: C.accentMuted } }}>
            GeoJSON
          </Button>
          <Button variant="outlined" onClick={handleExportPDF} startIcon={<PictureAsPdfRoundedIcon />}
            sx={{ borderColor: C.borderAccent, color: C.accentLight, borderRadius: '8px', textTransform: 'none', fontWeight: 600, '&:hover': { borderColor: C.accentLight, background: C.accentMuted } }}>
            Rapport PDF
          </Button>
          <Button disabled startIcon={<ScheduleRoundedIcon />}
            sx={{ borderColor: C.border, color: C.textMuted, borderRadius: '8px', textTransform: 'none', opacity: 0.5, border: `1px solid ${C.border}` }}>
            Planifier
            <Chip label="Bientôt" size="small" sx={{ ml: 1, background: C.accentMuted, color: C.accentLight, fontSize: '10px', height: 18 }} />
          </Button>
        </Box>
      </Box>

      {/* ── SECTION 1: Résumé de couverture ── */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <SummaryCard label="Total lieux collectés" value={loading ? '…' : rows.length} sub="Lieux approuvés" />
        <SummaryCard label="Spotters contributeurs" value={collecteursCount} sub="au moins un lieu approuvé, toutes dates" />
        <Box sx={{ flex: 2, minWidth: 280, background: C.card, border: `1px solid ${C.borderAccent}`, borderRadius: '8px', p: '18px 22px' }}>
          <Typography sx={{ color: C.textMuted, fontSize: '12px', mb: 1 }}>Répartition par fraîcheur</Typography>
          <FreshBar frais={freshCounts.frais} vieillissant={freshCounts.vieillissant} périmé={freshCounts.périmé} />
        </Box>
        <Box sx={{ flex: 1.5, minWidth: 220, background: C.card, border: `1px solid ${C.borderAccent}`, borderRadius: '8px', p: '18px 22px' }}>
          <Typography sx={{ color: C.textMuted, fontSize: '12px', mb: 1 }}>Top 3 types</Typography>
          {top3Types.map(({ type, total }) => (
            <Typography key={type} sx={{ fontSize: '12px', color: C.textSub, lineHeight: 1.8 }}>
              <span style={{ color: C.accentLight, fontWeight: 700 }}>{type}</span> : {total}
            </Typography>
          ))}
        </Box>
      </Box>

      {/* ── SECTION 2: Volume par dimension ── */}
      <Box sx={{ background: C.card, border: "1.5px solid #1F1E1B", borderRadius: '8px', mb: 3, overflow: 'hidden' }}>
        <Tabs
          value={activeTab}
          onChange={(_, v) => setActiveTab(v)}
          sx={{
            borderBottom: `1px solid ${C.border}`,
            '& .MuiTab-root': { color: C.textMuted, textTransform: 'none', fontWeight: 500 },
            '& .Mui-selected': { color: C.accentLight },
            '& .MuiTabs-indicator': { background: C.accentLight },
          }}
        >
          <Tab label="Par type de lieu" />
          <Tab label="Par zone géographique" />
          <Tab label="Par Spotter" />
          <Tab label="Par période" />
        </Tabs>

        <Box sx={{ p: 3 }}>
          {/* TAB 0: Par type */}
          {activeTab === 0 && (
            <Box sx={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr>
                    {['Type', 'Nb lieux', '% du total', 'Frais', 'Vieillissant', 'Périmé', 'Répartition'].map(h => (
                      <th key={h} style={{ textAlign: 'left', padding: '8px 12px', color: C.textMuted, fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.06em', borderBottom: `1px solid ${C.border}`, whiteSpace: 'nowrap' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {typeMap.map(({ type, total, pct, frais, vieillissant, périmé }) => (
                    <tr key={type}
                      style={{ borderBottom: `1px solid ${C.border}`, cursor: 'pointer' }}
                      onClick={() => setDrillType(type)}
                    >
                      <td style={{ padding: '10px 12px', color: C.accentLight, fontWeight: 600 }}>{type}</td>
                      <td style={{ padding: '10px 12px', color: C.text, fontWeight: 700 }}>{total}</td>
                      <td style={{ padding: '10px 12px', color: C.textSub }}>{pct}%</td>
                      <td style={{ padding: '10px 12px', color: '#356A0F' }}>{frais}</td>
                      <td style={{ padding: '10px 12px', color: '#7C5600' }}>{vieillissant}</td>
                      <td style={{ padding: '10px 12px', color: '#A23A0D' }}>{périmé}</td>
                      <td style={{ padding: '10px 12px', minWidth: 120 }}>
                        <Box sx={{ display: 'flex', height: 8, borderRadius: '4px', overflow: 'hidden', width: 100 }}>
                          {frais > 0 && <Box sx={{ width: `${(frais / total) * 100}%`, background: '#356A0F' }} />}
                          {vieillissant > 0 && <Box sx={{ width: `${(vieillissant / total) * 100}%`, background: '#7C5600' }} />}
                          {périmé > 0 && <Box sx={{ width: `${(périmé / total) * 100}%`, background: '#A23A0D' }} />}
                        </Box>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <Typography sx={{ fontSize: '11px', color: C.textMuted, mt: 1.5, fontStyle: 'italic' }}>
                Cliquer sur une ligne pour voir les lieux de ce type
              </Typography>
            </Box>
          )}

          {/* TAB 1: Par zone */}
          {activeTab === 1 && (
            <Box sx={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr>
                    {['Ville / Zone', 'Nb lieux', 'Spotters', 'Dernier lieu collecté'].map(h => (
                      <th key={h} style={{ textAlign: 'left', padding: '8px 12px', color: C.textMuted, fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.06em', borderBottom: `1px solid ${C.border}`, whiteSpace: 'nowrap' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {cityMap.map(({ city, count, collecteurs, lastDate }) => (
                    <tr key={city} style={{ borderBottom: `1px solid ${C.border}` }}>
                      <td style={{ padding: '10px 12px', color: C.accentLight, fontWeight: 600 }}>{city}</td>
                      <td style={{ padding: '10px 12px', color: C.text, fontWeight: 700 }}>{count}</td>
                      <td style={{ padding: '10px 12px', color: C.textSub }}>{collecteurs}</td>
                      <td style={{ padding: '10px 12px', color: C.textMuted }}>{lastDate}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Box>
          )}

          {/* TAB 2: Par Spotter */}
          {activeTab === 2 && (
            <Box sx={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr>
                    {['Spotter', 'Nb lieux', 'Dernière activité', ''].map(h => (
                      <th key={h} style={{ textAlign: 'left', padding: '8px 12px', color: C.textMuted, fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.06em', borderBottom: `1px solid ${C.border}`, whiteSpace: 'nowrap' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {collecteurMap.map(({ name, count, lastDate }, i) => (
                    <tr key={name} style={{ borderBottom: `1px solid ${C.border}` }}>
                      <td style={{ padding: '10px 12px', color: C.text, fontWeight: 600 }}>
                        {i === 0 && <span style={{ background: 'rgba(124,86,0,0.15)', color: '#7C5600', fontSize: '10px', fontWeight: 700, padding: '2px 6px', borderRadius: '4px', marginRight: 8 }}>🏆 Top</span>}
                        {name}
                      </td>
                      <td style={{ padding: '10px 12px', color: C.accentLight, fontWeight: 700 }}>{count}</td>
                      <td style={{ padding: '10px 12px', color: C.textMuted }}>{lastDate}</td>
                      <td style={{ padding: '10px 12px' }}>
                        <Box sx={{ display: 'flex', height: 6, borderRadius: '3px', overflow: 'hidden', width: 80, background: 'rgba(31,30,27,0.06)' }}>
                          <Box sx={{ width: `${Math.min((count / (collecteurMap[0]?.count || 1)) * 100, 100)}%`, background: C.accentLight }} />
                        </Box>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Box>
          )}

          {/* TAB 3: Par période */}
          {activeTab === 3 && (
            <Box>
              <Typography sx={{ fontSize: '13px', color: C.textMuted, mb: 2 }}>
                Lieux collectés par semaine ({filteredRows.length} lieux dans la sélection)
              </Typography>
              {weeklyData.length === 0 ? (
                <Typography sx={{ color: C.textMuted, textAlign: 'center', py: 4 }}>Aucune donnée disponible</Typography>
              ) : (
                <ResponsiveContainer width="100%" height={260}>
                  <BarChart data={weeklyData} margin={{ top: 4, right: 16, left: -20, bottom: 4 }}>
                    <XAxis dataKey="week" tick={{ fill: C.textMuted, fontSize: 11 }} />
                    <YAxis tick={{ fill: C.textMuted, fontSize: 11 }} />
                    <Tooltip
                      contentStyle={{ background: C.card2, border: `1px solid ${C.borderAccent}`, borderRadius: 6, color: C.text }}
                      cursor={{ fill: 'rgba(31,30,27,0.08)' }}
                    />
                    <Bar dataKey="count" radius={[4, 4, 0, 0]}>
                      {weeklyData.map((_, i) => (
                        <Cell key={i} fill={i === weeklyData.length - 1 ? C.accentLight : C.accent} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              )}
            </Box>
          )}
        </Box>
      </Box>

      {/* ── SECTION 3: Export ciblé ── */}
      <Box sx={{ background: C.card, border: "1.5px solid #1F1E1B", borderRadius: '8px', p: '20px 24px' }}>
        <Typography component="div" sx={{ fontWeight: 600, fontSize: '15px', color: C.text, mb: 2 }}>
          Export ciblé
          <Chip label={`${filteredRows.length} lieux`} size="small" sx={{ ml: 1.5, background: C.accentMuted, color: C.accentLight, fontWeight: 700, fontSize: '12px' }} />
        </Typography>

        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mb: 2 }}>
          <Select displayEmpty size="small" value={filterType} onChange={e => setFilterType(e.target.value)}
            sx={{ width: 170, background: C.card2, color: filterType ? C.text : C.textMuted, borderRadius: '8px', '& .MuiOutlinedInput-notchedOutline': { borderColor: C.border }, '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: C.borderAccent }, '& .MuiSvgIcon-root': { color: C.textMuted } }}>
            <MenuItem value=""><em style={{ color: C.textMuted }}>Tous les types</em></MenuItem>
            {uniqueTypes.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
          </Select>

          <TextField size="small" placeholder="Zone / Ville…" value={filterZone} onChange={e => setFilterZone(e.target.value)} sx={{ width: 150, ...inputSx }} />
          <TextField size="small" placeholder="Spotter…" value={filterCollecteur} onChange={e => setFilterCollecteur(e.target.value)} sx={{ width: 150, ...inputSx }} />

          <Select displayEmpty size="small" value={filterFreshness} onChange={e => setFilterFreshness(e.target.value)}
            sx={{ width: 165, background: C.card2, color: filterFreshness ? C.text : C.textMuted, borderRadius: '8px', '& .MuiOutlinedInput-notchedOutline': { borderColor: C.border }, '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: C.borderAccent }, '& .MuiSvgIcon-root': { color: C.textMuted } }}>
            <MenuItem value=""><em style={{ color: C.textMuted }}>Toute fraîcheur</em></MenuItem>
            <MenuItem value="frais">🟢 Frais (&lt;30j)</MenuItem>
            <MenuItem value="vieillissant">🟡 Vieillissant</MenuItem>
            <MenuItem value="périmé">🔴 Périmé</MenuItem>
          </Select>

          <TextField size="small" type="date" label="Depuis" InputLabelProps={{ shrink: true }} value={filterDateStart} onChange={e => setFilterDateStart(e.target.value)} sx={{ width: 155, ...inputSx }} />
          <TextField size="small" type="date" label="Jusqu'au" InputLabelProps={{ shrink: true }} value={filterDateEnd} onChange={e => setFilterDateEnd(e.target.value)} sx={{ width: 155, ...inputSx }} />

          <Button onClick={resetFilters} sx={{ color: C.textMuted, textTransform: 'none', borderRadius: '8px', border: `1px solid ${C.border}`, px: 2, height: 40, '&:hover': { borderColor: C.borderAccent, color: C.text } }}>
            Réinitialiser
          </Button>
        </Box>

        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
          {choixTypeExport}
          <Button variant="contained" onClick={handleExportCSV} startIcon={<DownloadIcon />}
            sx={{ background: C.accent, borderRadius: '9px', textTransform: 'none', fontWeight: 600, '&:hover': { background: C.accentLight } }}>
            Exporter CSV ({filteredRows.length})
          </Button>
          <Button variant="outlined" onClick={handleExportGeoJSON} startIcon={<DownloadIcon />}
            sx={{ borderColor: C.borderAccent, color: C.accentLight, borderRadius: '9px', textTransform: 'none', fontWeight: 600, '&:hover': { background: C.accentMuted } }}>
            GeoJSON
          </Button>
          <Button variant="outlined" onClick={handleExportPDF} startIcon={<PictureAsPdfRoundedIcon />}
            sx={{ borderColor: C.borderAccent, color: C.accentLight, borderRadius: '9px', textTransform: 'none', fontWeight: 600, '&:hover': { background: C.accentMuted } }}>
            Rapport PDF
          </Button>
        </Box>
      </Box>

      {/* ── Drill-down modal ── */}
      <Dialog
        open={Boolean(drillType)}
        onClose={() => setDrillType(null)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: { background: C.card, border: `1px solid ${C.borderAccent}`, borderRadius: '8px', color: C.text },
        }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: `1px solid ${C.border}`, pb: 1.5 }}>
          <Typography sx={{ fontWeight: 700, color: C.text }}>
            {drillType} — {drillRows.length} lieux
          </Typography>
          <IconButton onClick={() => setDrillType(null)} sx={{ color: C.textMuted }}>
            <CloseRoundedIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0, '& .MuiDataGrid-root': { background: 'transparent', color: C.text } }}>
          <DataGrid
            rows={drillRows}
            columns={drillColumns}
            initialState={{ pagination: { paginationModel: { page: 0, pageSize: 10 } } }}
            pageSizeOptions={[10, 25, 50]}
            sx={{
              background: 'transparent', border: 'none', color: C.text,
              '& .MuiDataGrid-columnHeaders': { background: 'rgba(31,30,27,0.02)', color: C.textMuted, fontSize: '11px', textTransform: 'uppercase', borderBottom: `1px solid ${C.border}` },
              '& .MuiDataGrid-row': { borderBottom: `1px solid ${C.border}`, color: C.text },
              '& .MuiDataGrid-row:hover': { background: 'rgba(31,30,27,0.02)' },
              '& .MuiDataGrid-cell': { borderBottom: 'none' },
              '& .MuiDataGrid-footerContainer': { background: 'transparent', borderTop: `1px solid ${C.border}`, color: C.textMuted },
              '& .MuiTablePagination-root': { color: C.textMuted },
              '& .MuiSvgIcon-root': { color: '#57534A' },
            }}
          />
        </DialogContent>
      </Dialog>
    </Box>
  );
}
